import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as S from './styles';
import { FormSelectProps, SelectOption } from './types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  required = false,
  error,
  inputSize = 'medium',
  labelPosition = 'left',
  width,
  leftIcon,
  helperText,
  containerClassName,
  fullWidth = false,
  disabled,
  value,
  onChange,
  options = [],
  placeholder,
  children,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    },
    [props]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      setTouched(true);
      props.onBlur?.(e);
    },
    [props]
  );

  // Função para focar no select quando a label for clicada
  const handleLabelClick = useCallback(() => {
    selectRef.current?.focus();
  }, []);

  const hasError = Boolean(error);
  const showRequiredError = required && (!value || value === '') && touched;
  const errorMessage = error || (showRequiredError ? 'Este campo é obrigatório' : '');
  const isErrorVisible = hasError || showRequiredError;

  const renderOptions = () => {
    if (children) {
      return children;
    }

    return (
      <>
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </>
    );
  };

  return (
    <S.FormSelectContainer
      labelPosition={labelPosition}
      fullWidth={fullWidth}
      width={width}
      className={containerClassName}
    >
      {label && (
        <S.SelectLabel
          onClick={handleLabelClick}
          htmlFor={props.id}
          required={required}
          labelPosition={labelPosition}
          hasError={hasError || showRequiredError}
        >
          {label}
        </S.SelectLabel>
      )}

      <S.SelectContainer
        inputSize={inputSize}
        hasError={hasError || showRequiredError}
      >
        {leftIcon && (
          <S.IconWrapper position="left">
            {leftIcon}
          </S.IconWrapper>
        )}

        <S.Select
          ref={selectRef}
          {...props}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          hasLeftIcon={Boolean(leftIcon)}
          inputSize={inputSize}
          hasError={hasError || showRequiredError}
          isFocused={isFocused}
          disabled={disabled}
          aria-invalid={hasError || showRequiredError}
          aria-required={required}
        >
          {renderOptions()}
        </S.Select>

        <S.ChevronIcon>
          <FontAwesomeIcon icon={faChevronDown} />
        </S.ChevronIcon>
      </S.SelectContainer>

      <S.ErrorMessage visible={isErrorVisible}>
        {errorMessage}
      </S.ErrorMessage>

      {helperText && !hasError && !showRequiredError && (
        <S.HelperText>{helperText}</S.HelperText>
      )}
    </S.FormSelectContainer>
  );
};

export default FormSelect;
